import { useEffect, useRef, useState } from "react";
import type { CaptureProgress, Report } from "../../lib/api/index.js";
import { outcomeLabel } from "../../lib/capture-outcomes.js";
import { shortDayLabel } from "../../lib/day-label.js";
import { useCaptureProgress } from "../../lib/queries/index.js";
import { Pause } from "../icons.js";
import { Bloom } from "../loader/index.js";
import { PausedMark } from "../paused-mark.js";

/** A quiet report is asked once a minute whether a batch has started. */
const IDLE_POLL_MS = 60_000;

/**
 * The brand's latest run, in the top bar (DESIGN.md §5, "The run pill").
 *
 * While captures run it is the mark assembling and how many are in; after,
 * the day and how many of the run answered. Pressed, it says what became of
 * the rest — because a capture without an answer is left out of every figure,
 * and a reader who cannot see that cannot tell "the engine left us out" from
 * "the engine never answered".
 */
export function RunPill({
  workspaceId,
  report,
}: {
  readonly workspaceId: string;
  readonly report: Report;
}) {
  const progress = useCaptureProgress(workspaceId, report.id, {
    idlePollMs: IDLE_POLL_MS,
  }).data;
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // Closes on a click anywhere else. A DOM listener, not data fetching.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", away);
    return () => {
      document.removeEventListener("mousedown", away);
    };
  }, [open]);

  // Paused says so in words, with the way back, rather than a run that never comes.
  if (report.paused) return <PausedMark brandName={report.brand.name} />;
  // Nothing read yet, or nothing ever planned: the pill has nothing true to say.
  if (progress?.runDate == null || progress.planned === 0) return null;

  const run = summarise(progress, progress.runDate);
  return (
    <div
      className="run-pill-wrap"
      ref={root}
      onKeyDown={(event) => {
        if (event.key === "Escape") setOpen(false);
      }}
    >
      <button
        type="button"
        className="run-pill"
        aria-expanded={open}
        aria-controls="run-card"
        title={run.held ? "This month's capture budget is used up" : undefined}
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {run.held ? (
          <Pause className="run-pill-mark run-pill-held" aria-hidden="true" />
        ) : run.pending > 0 ? (
          <Bloom size={16} motion="assemble" className="run-pill-mark" />
        ) : (
          <img
            className="run-pill-mark"
            src="/crux-mark.svg"
            alt=""
            width={16}
            height={16}
          />
        )}
        {run.held ? (
          <span>
            Waiting · <b>{run.finished}</b> of {run.planned}
          </span>
        ) : run.pending > 0 ? (
          <span>
            Asking · <b>{run.finished}</b> of {run.planned}
          </span>
        ) : (
          <span>
            {shortDayLabel(run.day)} · <b>{run.answered}</b> of {run.planned} answered
          </span>
        )}
      </button>
      {open ? <RunCard run={run} /> : null}
    </div>
  );
}

interface Run {
  readonly day: string;
  readonly planned: number;
  readonly pending: number;
  readonly answered: number;
  readonly finished: number;
  readonly without: number;
  readonly outcomes: CaptureProgress["unanswered"];
  readonly held: boolean;
}

function summarise(progress: CaptureProgress, day: string): Run {
  const without = progress.unanswered.reduce((sum, row) => sum + row.count, 0);
  return {
    day,
    planned: progress.planned,
    pending: progress.pending,
    answered: progress.answered,
    finished: progress.answered + without,
    without,
    outcomes: progress.unanswered.filter((row) => row.count > 0),
    held: progress.pending > 0 && progress.heldFor === "budget",
  };
}

/** What became of every capture in the run, opened from the pill. */
function RunCard({ run }: { readonly run: Run }) {
  return (
    <div className="run-card" id="run-card" role="group" aria-label="The latest run">
      <p className="run-card-label">Run of {shortDayLabel(run.day)}</p>
      <dl className="run-card-figures">
        <div>
          <dt>Answered</dt>
          <dd>{run.answered}</dd>
        </div>
        <div>
          <dt>Without an answer</dt>
          <dd>{run.without}</dd>
        </div>
        <div>
          <dt>{run.held ? "Waiting" : "Still asking"}</dt>
          <dd>{run.pending}</dd>
        </div>
      </dl>
      {run.outcomes.length === 0 ? null : (
        <ul className="run-card-outcomes">
          {run.outcomes.map((row) => (
            <li key={row.state}>
              <span>{sentenceCase(outcomeLabel(row.state))}</span>
              <b>{row.count}</b>
            </li>
          ))}
        </ul>
      )}
      <p className="run-card-note">
        {run.held
          ? "This month's capture budget is used up. The rest run when the month turns or the budget is raised. "
          : null}
        A capture without an answer is left out of every figure.
      </p>
    </div>
  );
}

function sentenceCase(words: string): string {
  return words.charAt(0).toUpperCase() + words.slice(1);
}
